import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@bookaburra/ui';
import { Paperclip, X, CheckCircle2 } from 'lucide-react';
import { Section } from '../components/Section.js';
import { usePageTitle } from '../components/usePageTitle.js';

// Shared with app/web: Turnstile site key. Same key used everywhere.
const TURNSTILE_SITE_KEY = '0x4AAAAAADP-JtFp_qN72yOV';
const API_BASE = 'https://app.bookaburra.com';

const MAX_FILES = 5;
const MAX_PER_FILE = 10 * 1024 * 1024;
const MAX_TOTAL = 20 * 1024 * 1024;

// Shared <select> styling. iOS/iPadOS Safari doesn't render the native
// drop-arrow on styled selects, so we paint a brand-sage chevron via
// a background-image SVG and reserve right padding for it.
const selectClass =
  "w-full appearance-none rounded-md border border-brand-sage/20 bg-brand-cream-shadow bg-[length:1em] bg-[right_0.75rem_center] bg-no-repeat py-2.5 pl-3 pr-9 text-sm text-brand-ink-soft focus:border-brand-sage focus:outline-none focus:ring-2 focus:ring-brand-sage/30 bg-[url(\"data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%27http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%27%20viewBox%3D%270%200%2020%2020%27%20fill%3D%27%235f7f66%27%3E%3Cpath%20fill-rule%3D%27evenodd%27%20d%3D%27M5.293%207.293a1%201%200%20011.414%200L10%2010.586l3.293-3.293a1%201%200%20111.414%201.414l-4%204a1%201%200%2001-1.414%200l-4-4a1%201%200%20010-1.414z%27%20clip-rule%3D%27evenodd%27%2F%3E%3C%2Fsvg%3E\")]";

export const Route = createFileRoute('/contact')({
  component: ContactPage,
});

interface FormState {
  name: string;
  email: string;
  businessName: string;
  industry: string;
  teamSize: string;
  phone: string;
  message: string;
}

const EMPTY: FormState = {
  name: '',
  email: '',
  businessName: '',
  industry: 'Medical imaging',
  teamSize: 'Solo',
  phone: '',
  message: '',
};

function ContactPage() {
  usePageTitle('Contact');
  const [form, setForm] = useState<FormState>(EMPTY);
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function addFiles(picked: FileList | File[]) {
    setError(null);
    const next = [...files];
    let total = next.reduce((a, f) => a + f.size, 0);
    for (const f of Array.from(picked)) {
      if (next.length >= MAX_FILES) {
        setError(`Up to ${MAX_FILES} files.`);
        break;
      }
      if (f.size > MAX_PER_FILE) {
        setError(`${f.name} is bigger than 10 MB.`);
        continue;
      }
      if (total + f.size > MAX_TOTAL) {
        setError('Total size over 20 MB.');
        break;
      }
      next.push(f);
      total += f.size;
    }
    setFiles(next);
  }

  function removeFile(i: number) {
    setFiles((f) => f.filter((_, idx) => idx !== i));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (!turnstileToken) {
      setError('Please complete the verification widget.');
      return;
    }
    if (!form.message.trim()) {
      setError('What’s the pain?');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const data = new FormData();
      data.set(
        'payload',
        JSON.stringify({
          name: form.name,
          email: form.email,
          businessName: form.businessName || undefined,
          industry: form.industry,
          teamSize: form.teamSize,
          phone: form.phone || undefined,
          message: form.message,
          turnstileToken,
          sourcePage: window.location.pathname,
        }),
      );
      for (const f of files) data.append('attachments', f);
      const res = await fetch(`${API_BASE}/api/conversations`, {
        method: 'POST',
        body: data,
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        setError(body.error || `Send failed (${res.status})`);
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Send failed');
      setSubmitting(false);
    }
  }

  return (
    <>
      {/* HERO */}
      <section className="bg-brand-cream">
        <div className="mx-auto max-w-6xl px-6 pb-16 pt-14 sm:px-12 sm:pt-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-brand-cream-shadow px-3 py-1 text-xs font-semibold uppercase tracking-widest text-brand-sage ring-1 ring-brand-sage/15">
            Contact
          </div>
          <h1 className="mt-6 max-w-3xl text-4xl font-extrabold leading-[1.05] tracking-tightest text-brand-ink-soft sm:text-6xl">
            Show us where booking admin <span className="text-brand-sage">takes time.</span>
          </h1>
          <p className="mt-7 max-w-2xl text-lg text-brand-ink-soft/80 sm:text-xl">
            We will tell you plainly whether Bookaburra fits your business.
          </p>
        </div>
      </section>

      <Section tone="shadow">
        <div className="mx-auto max-w-3xl">
          {done ? (
            <div className="rounded-2xl border border-emerald-300/60 bg-emerald-50 p-8 shadow-[0_30px_80px_-30px_rgba(19,33,36,0.15)]">
              <div className="flex items-center gap-2 text-emerald-900">
                <CheckCircle2 className="h-5 w-5" />
                <h2 className="text-xl font-bold">Sent</h2>
              </div>
              <p className="mt-3 text-sm text-emerald-900/80">
                Thanks for getting in touch. Bookaburra is a side project,
                so replies may be slow. I&rsquo;ll follow up when I can.
              </p>
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="rounded-2xl border border-brand-sage/15 bg-brand-cream p-8 shadow-[0_30px_80px_-30px_rgba(19,33,36,0.15)]"
              aria-label="Contact form"
            >
              <div className="grid gap-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Your name">
                    <input
                      type="text"
                      required
                      value={form.name}
                      onChange={(e) => update('name', e.target.value)}
                      className="w-full rounded-md border border-brand-sage/20 bg-brand-cream-shadow px-3 py-2.5 text-sm text-brand-ink-soft focus:border-brand-sage focus:outline-none focus:ring-2 focus:ring-brand-sage/30"
                      placeholder="Sam Robinson"
                    />
                  </Field>
                  <Field label="Email">
                    <input
                      type="email"
                      required
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      className="w-full rounded-md border border-brand-sage/20 bg-brand-cream-shadow px-3 py-2.5 text-sm text-brand-ink-soft focus:border-brand-sage focus:outline-none focus:ring-2 focus:ring-brand-sage/30"
                      placeholder="sam@example.com.au"
                    />
                  </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Business name (optional)">
                    <input
                      type="text"
                      value={form.businessName}
                      onChange={(e) => update('businessName', e.target.value)}
                      className="w-full rounded-md border border-brand-sage/20 bg-brand-cream-shadow px-3 py-2.5 text-sm text-brand-ink-soft focus:border-brand-sage focus:outline-none focus:ring-2 focus:ring-brand-sage/30"
                      placeholder="Coastal Imaging"
                    />
                  </Field>
                  <Field label="Phone (optional)">
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      className="w-full rounded-md border border-brand-sage/20 bg-brand-cream-shadow px-3 py-2.5 text-sm text-brand-ink-soft focus:border-brand-sage focus:outline-none focus:ring-2 focus:ring-brand-sage/30"
                      placeholder="04xx xxx xxx"
                    />
                  </Field>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="What are you running?">
                    <select
                      value={form.industry}
                      onChange={(e) => update('industry', e.target.value)}
                      className={selectClass}
                    >
                      <optgroup label="Health & wellness">
                        <option>Medical imaging</option>
                        <option>Clinics & allied health</option>
                        <option>Personal trainers</option>
                      </optgroup>
                      <optgroup label="Professional services">
                        <option>Accountants & tax agents</option>
                        <option>Mortgage brokers</option>
                        <option>Financial planners</option>
                      </optgroup>
                      <optgroup label="Home & property">
                        <option>Mobile trades</option>
                        <option>Pool care</option>
                        <option>Gardeners & landscapers</option>
                      </optgroup>
                      <optgroup label="Other services">
                        <option>Mobile dog groomers</option>
                        <option>Independent mechanics</option>
                      </optgroup>
                      <option>Something else</option>
                    </select>
                  </Field>
                  <Field label="Team size">
                    <select
                      value={form.teamSize}
                      onChange={(e) => update('teamSize', e.target.value)}
                      className={selectClass}
                    >
                      <option>Solo</option>
                      <option>2–5</option>
                      <option>6–20</option>
                      <option>20+</option>
                    </select>
                  </Field>
                </div>
                <Field label="What’s the booking pain?">
                  <textarea
                    rows={5}
                    required
                    value={form.message}
                    onChange={(e) => update('message', e.target.value)}
                    maxLength={10_000}
                    className="w-full rounded-md border border-brand-sage/20 bg-brand-cream-shadow px-3 py-2.5 text-sm text-brand-ink-soft focus:border-brand-sage focus:outline-none focus:ring-2 focus:ring-brand-sage/30"
                    placeholder="Example: clients book the wrong room, travel time gets missed, or staff swaps take too long."
                  />
                </Field>

                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    hidden
                    onChange={(e) => {
                      if (e.target.files) addFiles(e.target.files);
                      e.target.value = '';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-1.5 rounded-md border border-brand-sage/20 bg-brand-cream-shadow px-3 py-1.5 text-xs font-medium text-brand-ink-soft hover:bg-brand-cream"
                  >
                    <Paperclip className="h-3.5 w-3.5" />
                    Attach screenshots
                  </button>
                  {files.length > 0 ? (
                    <ul className="mt-2 space-y-1">
                      {files.map((f, i) => (
                        <li
                          key={`${f.name}-${i}`}
                          className="flex items-center justify-between rounded-md bg-brand-cream-shadow px-2 py-1 text-xs text-brand-ink-soft"
                        >
                          <span className="truncate">
                            {f.name}{' '}
                            <span className="text-brand-ink-soft/55">({formatBytes(f.size)})</span>
                          </span>
                          <button
                            type="button"
                            onClick={() => removeFile(i)}
                            aria-label={`Remove ${f.name}`}
                            className="ml-2 text-brand-ink-soft/55 hover:text-rose-600"
                          >
                            <X className="h-3.5 w-3.5" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>

                <TurnstileWidget onToken={setTurnstileToken} />

                {error ? (
                  <p className="text-xs text-rose-700">{error}</p>
                ) : null}

                <Button type="submit" size="lg" className="w-full" disabled={submitting}>
                  {submitting ? 'Sending…' : 'Send'}
                </Button>
              </div>
            </form>
          )}
        </div>
      </Section>
    </>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-brand-ink-soft">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

/**
 * Lightweight Turnstile widget loader. Loads the challenges script
 * once, renders the widget into a div, and reports the token via
 * `onToken`. Token expires after ~5 minutes; we listen for the
 * automatic re-render and forward the fresh token.
 */
function TurnstileWidget({ onToken }: { onToken: (t: string | null) => void }) {
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const SCRIPT_ID = 'cf-turnstile-script';
    function render() {
      const ts = (window as unknown as { turnstile?: TurnstileGlobal }).turnstile;
      if (!ts || !ref.current) return;
      try {
        ts.render(ref.current, {
          sitekey: TURNSTILE_SITE_KEY,
          // Force light theme — Bookaburra's UI is cream-on-sage regardless
          // of OS dark mode, so the default 'auto' makes the widget jarringly
          // dark at night.
          theme: 'light',
          callback: (token: string) => onToken(token),
          'expired-callback': () => onToken(null),
          'error-callback': () => onToken(null),
        });
      } catch {
        // already rendered
      }
    }

    if (document.getElementById(SCRIPT_ID)) {
      render();
      return;
    }
    const script = document.createElement('script');
    script.id = SCRIPT_ID;
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
    script.async = true;
    script.defer = true;
    script.onload = render;
    document.head.appendChild(script);
  }, [onToken]);

  return <div ref={ref} className="cf-turnstile" />;
}

interface TurnstileGlobal {
  render: (
    el: HTMLElement,
    options: {
      sitekey: string;
      theme?: 'light' | 'dark' | 'auto';
      callback: (token: string) => void;
      'expired-callback'?: () => void;
      'error-callback'?: () => void;
    },
  ) => void;
}
